/**
 * Schema Phone Field
 *
 * How a schema text field that writes the player's mobile number (`system` with
 * `target: mobilePhone`) is drawn: as the registration form's phone field — flag and dial
 * code beside the number, the same mask, hint and error texts — but with the dial code
 * fixed.
 *
 * Fixed at +1: the only card that writes the number is Interac, which is Canadian, and its
 * schema accepts +1 numbers only. A code the player could change would only offer ways to
 * fail.
 *
 * The input shows the national number as the player types it, "(416) 555-1234"; value() is
 * what the schema and the server check, "+14165551234". SchemaRenderer reads that, never
 * the input.
 *
 * @package betbeast
 */

import { applyMask, removeMask } from '../../auth-flow-components/phone-input.js';
import { validatePhone } from '../../modules/field-validators.js';
import { phoneCountryHint, phoneCountryMessage, phoneCountryVerdict } from '../../modules/phone-country-rules.js';

/**
 * The fixed dial code, its region, and the national number's length and mask (the mask
 * registration uses for Canada, in COUNTRY_PHONE_DATA, when that is on the page).
 */
const DIAL = {
    code:   '1',
    region: 'CA',
    name:   'Canada',
    digits: 10,
    mask:   '(___) ___-____',
};

/**
 * Whether a schema field is drawn as the phone field.
 *
 * @param {Object} field
 * @returns {boolean}
 */
export function isPhoneField( field ) {
    return !! field?.system && field.target === 'mobilePhone' && ( field.frontend_component ?? 'text' ) === 'text';
}

/**
 * Registration's phone rules and texts (accountData.validationRules.phone); empty when the
 * page has none, so the English fallbacks of validatePhone() apply.
 *
 * @returns {Object}
 */
function phoneRules() {
    return window.accountData?.validationRules?.phone ?? {};
}

export class SchemaPhoneField {

    /**
     * @param {HTMLInputElement} input  Built by the renderer (id, name, data-schema-field).
     * @param {Object}           field  Schema field.
     * @param {string}           seed   The player's number as stored, already checked
     *                                  against the field's rules; '' for none.
     * @param {Function}         check  ( value ) => the schema's own error for a value, ''
     *                                  when it passes — the check the server repeats.
     */
    constructor( input, field, seed, check ) {
        this.input = input;
        this.field = field;
        this.check = check;
        this.mask  = window.COUNTRY_PHONE_DATA?.[ DIAL.region ]?.mask || DIAL.mask;

        this.root = this._build();
        this.input.value = applyMask( this._national( seed ), this.mask );
    }

    // -------------------------------------------------------------------------
    // Building
    // -------------------------------------------------------------------------

    _build() {
        const root = document.createElement( 'div' );
        root.className = 'schema-phone';

        // Not a button: the code cannot be changed, so nothing about it looks clickable.
        const prefix = document.createElement( 'span' );
        prefix.className = 'schema-phone__prefix';

        const flag     = document.createElement( 'img' );
        flag.className = 'schema-phone__flag';
        flag.src       = `${ window.accountData?.themeUrl || '/app/themes/betbeast' }/dist/images/flags-webp/${ DIAL.region.toLowerCase() }.webp`;
        flag.alt       = '';
        flag.width     = 24;
        flag.height    = 18;
        flag.decoding  = 'async';

        const code       = document.createElement( 'span' );
        code.className   = 'schema-phone__code';
        code.textContent = `+${ DIAL.code }`;

        prefix.append( flag, code );

        const number     = document.createElement( 'span' );
        number.className = 'schema-phone__number';

        this.input.type        = 'tel';
        this.input.placeholder = this.field.label;
        this.input.setAttribute( 'inputmode', 'tel' );
        this.input.setAttribute( 'autocomplete', 'tel-national' );

        // One handler, as in registration: the mask is applied again on every change.
        this.input.addEventListener( 'input', () => {
            this.input.value = applyMask( this._digits(), this.mask );
        } );

        number.append( this.input );
        root.append( prefix, number );

        return root;
    }

    /**
     * Put the example number in the floating label after the field's name, as registration
     * does: "Mobile number · e.g. 506-234-5678". Only while country phone rules are on.
     * Called after FloatingLabel has built the label.
     */
    updateHint() {
        const countryRules = phoneRules().country_rules;
        if ( ! countryRules ) return;

        const label = this.input.closest( '.field' )?.querySelector( '.field__floating-label' );
        if ( ! label ) return;

        const name = this.input.getAttribute( 'placeholder' ) || '';
        const hint = phoneCountryHint( DIAL.code, countryRules, DIAL.region, true );
        label.textContent = hint ? `${ name } · ${ hint }` : name;
    }

    // -------------------------------------------------------------------------
    // Value and validation
    // -------------------------------------------------------------------------

    /**
     * The number in the form the schema checks, "+14165551234"; '' when nothing is typed.
     *
     * @returns {string}
     */
    value() {
        const digits = this._digits();
        return digits ? `+${ DIAL.code }${ digits }` : '';
    }

    /**
     * Registration's texts and kinds (red error, amber warning) for this one dial code. The
     * country rule's "You can still continue" wording is never used: here the number has to
     * pass, or the payment is refused.
     *
     * Then the schema's own rules, on the value that is sent ("+1" and ten digits, twelve
     * characters): what fails there fails on the server too, so it gets the schema's text,
     * the one the server answers with.
     *
     * @returns {{ valid: boolean|null, message: string, type: string }}
     */
    validate() {
        const digits = this._digits();
        const rules  = phoneRules();

        if ( ! digits ) {
            // An optional field left empty is not sent at all.
            return this.field.required
                ? validatePhone( '', rules )
                : { valid: null, message: '', type: '' };
        }

        const length = validatePhone( digits, { ...rules, min_digits: DIAL.digits, max_digits: DIAL.digits } );
        if ( ! length.valid ) return length;

        const phone   = this.value();
        const verdict = rules.country_rules
            ? phoneCountryVerdict( phone, 'account', rules.country_rules, DIAL.region )
            : null;

        if ( 'flagged' === verdict?.status ) {
            return { valid: false, message: this._invalidMessage( verdict ), type: 'error' };
        }

        const schemaError = this.check( phone );
        if ( schemaError ) {
            return { valid: false, message: schemaError, type: 'error' };
        }

        return { valid: true, message: '', type: 'valid' };
    }

    /**
     * @param {Object|null} verdict
     * @returns {string}
     */
    _invalidMessage( verdict ) {
        const rules = phoneRules();

        if ( rules.country_rules ) {
            const blocked = { ...( verdict ?? {} ), region: DIAL.region, block: true };
            const names   = window.COUNTRY_PHONE_DATA ?? { [ DIAL.region ]: { name: DIAL.name } };
            const text    = phoneCountryMessage( blocked, rules.country_rules, names );
            if ( text ) return text;
        }

        return rules.error_messages?.invalid || 'Please enter a valid phone number.';
    }

    /**
     * The national digits typed. Eleven starting with the dial code are a number pasted or
     * autofilled with it ("+1 416 555 1234"): the code is dropped, it is already beside the
     * field.
     *
     * @returns {string}
     */
    _digits() {
        return this._national( this.input.value );
    }

    /**
     * @param {string} value
     * @returns {string}
     */
    _national( value ) {
        const digits = removeMask( String( value ?? '' ) );
        return digits.length === DIAL.code.length + DIAL.digits && digits.startsWith( DIAL.code )
            ? digits.substring( DIAL.code.length )
            : digits;
    }
}
